import { ChevronDown, CircleCheck, Ellipsis, Download, FolderOpen, History, TriangleAlert, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { PlanFile } from "@mcp/plan";
import { controller } from "../app/controller.ts";
import { useApp } from "../state/store.ts";
import { isDesktop } from "../lib/desktop.ts";

const SAVE_LABEL = {
  local: { text: "Saved locally", cls: "ok", tip: "Every change is saved automatically in this browser. Use File, then Save plan file (Ctrl+S) for a copy you can move to another PC." },
  syncing: { text: "Syncing…", cls: "busy", tip: "Catching up with the live session." },
  synced: { text: "Saved & synced", cls: "ok", tip: "Saved in this browser and on the server — everyone in the session has it." },
  offline: { text: "Offline changes", cls: "warn", tip: "The live connection dropped. Your changes are kept in this browser and sync as soon as it's back." },
} as const;

function ago(t: number) {
  const s = Math.round((Date.now() - t) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return new Date(t).toLocaleDateString();
}

/** Save status chip + File menu (plan files, versions). */
export function FileMenu() {
  const saveState = useApp((s) => s.saveState);
  const versions = useApp((s) => s.versions);
  const [open, setOpen] = useState(false);
  const [versionName, setVersionName] = useState("");
  const [pending, setPending] = useState<{ name: string; data: PlanFile } | null>(null);
  const [confirmRestore, setConfirmRestore] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [withImages, setWithImages] = useState(true);
  const fileInput = useRef<HTMLInputElement>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    const off = (e: PointerEvent) => { if (!wrap.current?.contains(e.target as Node)) { setOpen(false); setConfirmRestore(null); } };
    window.addEventListener("pointerdown", off);
    return () => window.removeEventListener("pointerdown", off);
  }, [open]);
  const lbl = SAVE_LABEL[saveState];

  const flash = (msg: string) => { setNote(msg); setTimeout(() => setNote(null), 3000); };

  const pick = async (f: File) => {
    try {
      setPending({ name: f.name, data: await controller.readPlanFile(f) });
      setOpen(false);
    } catch (e) {
      useApp.getState().set({ error: `Can't load ${f.name}: ${(e as Error).message}` });
    }
  };

  const saveVersion = () => {
    const v = controller.saveVersion(versionName || `Version ${new Date().toLocaleString()}`);
    setVersionName("");
    if (v) flash(`Saved version “${v.name}”`);
  };

  return (
    <div className="file-group" ref={wrap}>
      <span className={`save-chip ${lbl.cls}`} title={lbl.tip} role="status"><i className="dot" />{lbl.text}</span>
      <button ref={btn} className="btn btn-ghost file-btn" aria-pressed={open} onClick={() => setOpen(!open)} title="Save / load plan files and versions" aria-haspopup="menu" aria-expanded={open}><Ellipsis className="file-ico" size={16} aria-hidden="true" />File<ChevronDown className="chev" size={14} aria-hidden="true" /></button>
      {note && <div className="toast ok-toast" role="status"><CircleCheck size={16} aria-hidden="true" />{note}</div>}
      {open && (
        <div className="menu file-menu" role="menu">
          <button role="menuitem" className="menu-item" onClick={() => { void controller.savePlanFile(withImages); setOpen(false); }}>
            <Download size={15} aria-hidden="true" />
            <span className="grow">Save plan file…<span className="menu-sub">{isDesktop() ? "A .mcplan file in Documents\\MC Planner" : "Downloads a .mcplan file"} with all plans, pinboards, settings and versions</span></span>
            <span className="menu-keys"><kbd>Ctrl</kbd><kbd>S</kbd></span>
          </button>
          <label className="menu-opt"><input type="checkbox" checked={withImages} onChange={(e) => setWithImages(e.target.checked)} /><span>Include pinboard images <span className="menu-sub">Bigger file, but boards work on another PC</span></span></label>
          <button role="menuitem" className="menu-item" onClick={() => fileInput.current?.click()}>
            <FolderOpen size={15} aria-hidden="true" />
            <span className="grow">Load plan file…<span className="menu-sub">.mcplan or an older .json export — you choose replace or add</span></span>
          </button>
          <div className="menu-sep" />
          <div className="menu-title"><span className="overline">Versions</span><span className="caption">Shared with everyone in this world</span></div>
          <form className="version-new" onSubmit={(e) => { e.preventDefault(); saveVersion(); }}>
            <input className="input" placeholder="Name, e.g. Before the castle" value={versionName} onChange={(e) => setVersionName(e.target.value)} aria-label="Version name" />
            <button type="submit" className="btn btn-sm btn-primary">Save version</button>
          </form>
          <div className="version-list">
            {versions.length === 0 && <p className="menu-empty"><History size={15} aria-hidden="true" />No versions yet. Save one before big changes — you can always go back.</p>}
            {versions.map((v) => (
              <div key={v.id} className="version-item">
                <div className="grow">
                  <b>{v.name}</b>
                  <span className="menu-sub">{ago(v.createdAt)}{v.author ? ` · ${v.author}` : ""} · {v.count ?? 0} objects</span>
                </div>
                {confirmRestore === v.id ? (
                  <>
                    <button className="btn btn-sm btn-primary" onClick={() => { controller.restoreVersion(v.id); setConfirmRestore(null); setOpen(false); flash(`Restored “${v.name}” — Ctrl+Z undoes it`); }}>Restore now</button>
                    <button className="btn btn-sm btn-ghost" onClick={() => setConfirmRestore(null)}>Cancel</button>
                  </>
                ) : (
                  <>
                    <button className="btn btn-sm btn-outline" onClick={() => setConfirmRestore(v.id)} title="Replace the current plan with this version (undoable)">Restore</button>
                    <button className="btn btn-sm btn-ghost btn-icon" onClick={() => controller.deleteVersion(v.id)} title="Delete this version" aria-label="Delete this version"><X size={14} aria-hidden="true" /></button>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      <input ref={fileInput} type="file" accept=".mcplan,.json,application/json" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) void pick(f); e.target.value = ""; }} />
      {pending && <LoadDialog file={pending} onClose={() => setPending(null)} onDone={flash} />}
    </div>
  );
}

function LoadDialog({ file, onClose, onDone }: { file: { name: string; data: PlanFile }; onClose: () => void; onDone: (m: string) => void }) {
  const current = useApp((s) => s.plans.length);
  const level = useApp((s) => s.level);
  const d = file.data;
  const otherWorld = d.world?.name && level?.name && d.world.name !== level.name;
  const load = (mode: "replace" | "merge") => {
    const r = controller.loadPlanFile(d, mode);
    onClose();
    if (r) onDone(`${mode === "replace" ? "Loaded" : "Added"} ${r.objects} objects${r.versions ? ` and ${r.versions} versions` : ""} — Ctrl+Z undoes it`);
  };
  return (
    <div className="modal-back" onClick={onClose}>
      <div className="modal load-dialog" role="dialog" aria-label="Load plan file" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <span className="modal-icon"><FolderOpen size={18} aria-hidden="true" /></span>
          <div className="grow">
            <h3>Load plan file</h3>
            <p className="caption"><b className="file-name">{file.name}</b></p>
          </div>
          <button className="btn btn-ghost btn-icon" onClick={onClose} title="Close · Esc" aria-label="Close"><X size={16} aria-hidden="true" /></button>
        </div>
        <p className="caption tabular">
          {d.objects.length} objects{d.versions?.length ? ` · ${d.versions.length} versions` : ""}
          {d.world?.name ? ` · world “${d.world.name}”` : ""}
          {d.savedAt ? ` · saved ${new Date(d.savedAt).toLocaleString()}` : ""}
        </p>
        {otherWorld && <p className="callout warn"><TriangleAlert size={15} aria-hidden="true" />This file was saved for another world (“{d.world.name}”). Coordinates may not fit this map.</p>}
        <div className="choice">
          <button className="choice-item primary-choice" onClick={() => load("replace")}>
            <b>Replace current plan</b>
            <span>{current ? `Your ${current} objects are replaced. A version “Before loading a plan file” is saved first.` : "Your plan is empty, nothing is lost."}</span>
          </button>
          <button className="choice-item" onClick={() => load("merge")}>
            <b>Add to current plan</b>
            <span>Keeps everything and adds the file's objects.</span>
          </button>
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
